import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraToolRun } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { classifyToolResult, formatDuration, getToolConfig, humanizeToolId } from '../../../lib/tool-configs';
import { getRemoteToolConfig, useRemoteCatalog } from '../../../lib/tool-configs/remote';
import { ToolCard } from '../../tools';
import { useChatReadOnly } from '../ChatFeedbackContext';
import { StaleBundleCardHint } from '../StaleBundleCardHint';
import { AskUserCard } from '../shanks-tools/AskUserCard';
import { CreateOfficeDocumentCard, isOfficeMintTool } from '../shanks-tools/CreateOfficeDocumentCard';
import { DeliveryActionCard } from '../shanks-tools/DeliveryActionCard';
import { DiscoverToolsCard } from '../shanks-tools/DiscoverToolsCard';
import { DocumentCreateCard } from '../shanks-tools/DocumentCreateCard';
import { DocumentWriteCard } from '../shanks-tools/DocumentWriteCard';
import { DownloadFileCard } from '../shanks-tools/DownloadFileCard';
import { DeliverableImportCard, parseDeliverableImportCard } from '../shanks-tools/deliverable-import';
import { EvidenceBatchCard } from '../shanks-tools/EvidenceBatchCard';
import { MemorySavedLine, memorySaved } from '../shanks-tools/MemorySavedLine';
import { PublishCard } from '../shanks-tools/PublishCard';
import { ReadDocumentChunksCard } from '../shanks-tools/ReadDocumentChunksCard';
import { RecordWriteCard } from '../shanks-tools/RecordWriteCard';
import { RequestToolsCard } from '../shanks-tools/RequestToolsCard';
import { SopWriteCard } from '../shanks-tools/SopWriteCard';
import { unrecognizedToolResult } from '../staleBundleCard';
import { CorporateFamilyTreeToolCall, isCorporateFamilyTreeErrorOutput, isCorporateFamilyTreeOutput } from './tools/CorporateFamilyTreeToolCall';
import { GenerateMermaidDiagramToolCall, isGenerateMermaidDiagramOutput } from './tools/GenerateMermaidDiagramToolCall';
/**
 * Convert MessagePartTool to ShanksToolCall format for Shanks tool components
 */ const WORKROOM_OUTCOME_TOOLS = new Set([
    'create-office-document',
    'sam-awards-query',
    'sam-track-vendor-footprint',
    'sam-find-incumbents',
    'sam-get-contract-rundown',
    'sam-recompete-forecasting',
    'generateMermaidDiagramTool',
    'generate-mermaid-diagram',
    'generateMermaidDiagram',
    'corporate-family-tree',
    'corporateFamilyTreeTool'
]);
export function isWorkroomOutcomeTool(part) {
    const toolName = part.toolName || part.type.replace(/^tool-?/, '');
    if (part.output && typeof part.output === 'object') {
        const out = part.output;
        if ('_documentWrite' in out || '_recordWrite' in out || '_requiresApproval' in out) return true;
        // A download card is the file's only handle: never folded into a lookup run.
        if (out._download === true && out.ok !== false) return true;
        if (memorySaved(out)) return true;
        if (isOfficeMintTool(toolName) && typeof out.documentId === 'string') return true;
    }
    if (parseDeliverableImportCard(part.output)) return true;
    return WORKROOM_OUTCOME_TOOLS.has(toolName);
}
// Adjacent lookup runs collapse into one disclosure. Writes / code exec keep
// their own ToolCard — batching those hid create-office-document / execute-python.
export function isWorkroomLookupTool(part) {
    if (isWorkroomOutcomeTool(part)) return false;
    const name = (part.toolName || part.type.replace(/^tool-?/, '')).toLowerCase().replace(/_/g, '-');
    return !/^(create|write|execute|generate|apply|update|delete|archive)(-|$)/.test(name);
}
function toolDisplayName(part) {
    const toolName = part.toolName || part.type.replace(/^tool-?/, '');
    return getToolConfig(toolName)?.displayName ?? getRemoteToolConfig(toolName)?.displayName ?? humanizeToolId(toolName);
}
export function SpecraToolBatch({ parts, messageId }) {
    useRemoteCatalog((state)=>state.configs);
    const names = parts.map(toolDisplayName);
    const anyRunning = parts.some((p)=>p.state === 'input-streaming' || p.state === 'input-available');
    const anyError = parts.some((p)=>{
        const name = p.toolName || p.type.replace(/^tool-?/, '');
        const config = getToolConfig(name);
        if (config) return classifyToolResult(config, p.output, false) === 'error';
        return Boolean(p.output?.error);
    });
    const title = parts.length === 1 ? names[0] : `${parts.length} tools`;
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.specraChat.toolRun,
        children: /*#__PURE__*/ _jsx(SpecraToolRunPart, {
            displayName: title,
            isRunning: anyRunning,
            isError: anyError,
            settledSummary: names.join(' · '),
            children: /*#__PURE__*/ _jsx("div", {
                className: "flex flex-col gap-1.5",
                children: parts.map((part, i)=>{
                    const err = typeof part.output === 'object' && part.output && 'error' in part.output ? String(part.output.error ?? '') : '';
                    return /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                children: names[i]
                            }),
                            err ? /*#__PURE__*/ _jsx("div", {
                                className: "mt-0.5",
                                children: err
                            }) : null
                        ]
                    }, part.toolCallId || `${messageId}-batch-${i}`);
                })
            })
        })
    });
}
function toShanksToolCall(part) {
    const toolName = part.toolName || part.type.replace(/^tool-?/, '');
    return {
        id: toolName,
        name: toolName,
        result: part.output ? JSON.stringify(part.output) : undefined,
        error: part.output?.error
    };
}
/**
 * S7 — in the Workroom, a tool run reads as a collapsible Specra disclosure: a
 * one-line bar (status dot + tool name + status) that expands to the tool's
 * detail. It stays collapsed while running and on success so the conversation
 * reads cleanly, and pops open on error so the failure is visible. The status
 * word ("Running…" / "Error") carries the state as text too, so it isn't
 * conveyed by the dot colour alone.
 */ function SpecraToolRunPart({ displayName, isRunning, isError, duration, children, settledSummary }) {
    const tone = isRunning ? 'info' : isError ? 'danger' : 'success';
    // The summary carries the state as text ("Running…" / "Error") so it isn't
    // conveyed by the dot colour alone (a11y). A settled success shows its
    // duration there instead, keeping the /assistant card's duration readout that
    // the embedded card's hidden header would otherwise drop.
    const summary = isRunning ? 'Running…' : isError ? 'Error' : settledSummary ?? (duration !== undefined ? formatDuration(duration) : undefined);
    return /*#__PURE__*/ _jsx(SpecraToolRun, {
        title: displayName,
        summary: summary,
        tone: tone,
        open: isError,
        "data-testid": Testids.specraChat.toolRun,
        "data-state": isRunning ? 'running' : isError ? 'error' : 'success',
        children: children
    });
}
/**
 * Renders a tool call message part
 * NOTE: Not memoized - output might stream in or state might change
 */ export function ToolPart({ part, messageId, index, variant = 'default' }) {
    const readOnly = useChatReadOnly();
    useRemoteCatalog((state)=>state.configs);
    const toolName = part.toolName || part.type.replace(/^tool-?/, '');
    const isRunning = part.state === 'input-streaming' || part.state === 'input-available';
    if (unrecognizedToolResult(part.output)) {
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(StaleBundleCardHint, {})
        }, `${messageId}-tool-${index}`);
    }
    // Check if this is a document write requiring approval
    const isDocumentWrite = part.output && typeof part.output === 'object' && '_documentWrite' in part.output;
    const ask = part.output;
    if (ask && typeof ask === 'object' && ask._askUser) {
        // The agent's questions: the run waits for the answers.
        return /*#__PURE__*/ _jsx("div", {
            className: "min-w-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(AskUserCard, {
                output: ask,
                toolCallId: part.toolCallId
            })
        }, `${messageId}-tool-${index}`);
    }
    const download = part.output;
    if (download && typeof download === 'object' && download._download && download.ok !== false) {
        // A genuine export: a file to download, nothing saved, no Accept.
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(DownloadFileCard, {
                output: download
            })
        }, `${messageId}-tool-${index}`);
    }
    if (isDocumentWrite && part.output._publish) {
        // Publish: shared or saved-for-the-record actions run only on Accept.
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(PublishCard, {
                output: part.output,
                toolCallId: part.toolCallId
            })
        }, `${messageId}-tool-${index}`);
    }
    if (isDocumentWrite && part.output._documentCreate) {
        // Create mode: a document chat proposes; it exists only after Accept.
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(DocumentCreateCard, {
                output: part.output,
                toolCallId: part.toolCallId
            })
        }, `${messageId}-tool-${index}`);
    }
    if (isDocumentWrite) {
        const output = part.output;
        const legacyPartId = `${messageId}-tool-${index}`;
        // Streamed message ids and part positions change when history is loaded.
        // The document and provider call id identify the same proposal in both forms.
        const decisionId = part.toolCallId ? `document-write:${output.applyDocumentId || output.documentId || ''}:${part.toolCallId}` : legacyPartId;
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(DocumentWriteCard, {
                output: output,
                partId: decisionId,
                legacyPartId: legacyPartId,
                toolCallId: part.toolCallId,
                requireSavedDecision: true
            }, decisionId)
        }, `${messageId}-tool-${index}`);
    }
    // Check if this is an AI-proposed record/status change requiring approval (R03).
    const isRecordWrite = part.output && typeof part.output === 'object' && '_recordWrite' in part.output;
    const saved = memorySaved(part.output);
    if (saved) {
        // The assistant kept a note: say where, with Undo for a new personal or project note.
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(MemorySavedLine, {
                saved: saved
            })
        }, `${messageId}-tool-${index}`);
    }
    const sopWrite = part.output;
    if (sopWrite && typeof sopWrite === 'object' && sopWrite._sopWrite) {
        // An SOP the agent proposes: saved only on Accept, under the human's principal.
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(SopWriteCard, {
                output: sopWrite
            })
        }, `${messageId}-tool-${index}`);
    }
    if (isRecordWrite) {
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(RecordWriteCard, {
                output: part.output,
                toolCallId: part.toolCallId
            })
        }, `${messageId}-tool-${index}`);
    }
    // Deliverable evidence staged by stage-evidence-batch: one run behind Accept.
    if (part.output && typeof part.output === 'object' && '_evidenceBatch' in part.output) {
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(EvidenceBatchCard, {
                output: part.output,
                toolCallId: part.toolCallId
            })
        }, `${messageId}-tool-${index}`);
    }
    // SAD-870: Delivery mutations proposed through propose-delivery-action.
    const isDeliveryAction = part.output && typeof part.output === 'object' && '_deliveryAction' in part.output;
    if (isDeliveryAction) {
        const deliveryPartId = `delivery-action:${part.toolCallId || `${messageId}-tool-${index}`}`;
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(DeliveryActionCard, {
                output: part.output,
                toolCallId: part.toolCallId,
                partId: deliveryPartId,
                requireSavedDecision: true
            })
        }, `${messageId}-tool-${index}`);
    }
    // Deliverable import review journey (SAD-656): shanks emits a
    // _deliverableImportCard wrapper for each step; render it directly so it's
    // never collapsed into a generic tool disclosure.
    const deliverableImportPayload = parseDeliverableImportCard(part.output);
    if (deliverableImportPayload) {
        return /*#__PURE__*/ _jsx("fieldset", {
            disabled: readOnly,
            className: "min-w-0 border-0 p-0",
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(DeliverableImportCard, {
                payload: deliverableImportPayload
            })
        }, `${messageId}-tool-${index}`);
    }
    // Lookup is not the default OS, but when the model confers a playbook
    // or searches the catalog because it is stuck, show it — that builds trust.
    const shanksRenderers = {
        discover_tools: /*#__PURE__*/ _jsx(DiscoverToolsCard, {
            toolCall: toShanksToolCall(part)
        }),
        request_tools: /*#__PURE__*/ _jsx(RequestToolsCard, {
            toolCall: toShanksToolCall(part)
        }),
        'read-document-chunks': /*#__PURE__*/ _jsx(ReadDocumentChunksCard, {
            toolCall: toShanksToolCall(part)
        }),
        read_document_chunks: /*#__PURE__*/ _jsx(ReadDocumentChunksCard, {
            toolCall: toShanksToolCall(part)
        })
    };
    if (toolName in shanksRenderers) {
        const card = shanksRenderers[toolName];
        if (variant === 'specra') {
            return /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.specraChat.toolRun,
                children: /*#__PURE__*/ _jsx(SpecraToolRunPart, {
                    displayName: toolDisplayName(part),
                    isRunning: isRunning,
                    isError: Boolean(part.output?.error),
                    duration: part.duration,
                    children: card
                })
            }, `${messageId}-tool-${index}`);
        }
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.assistant.messageTool,
            children: card
        }, `${messageId}-tool-${index}`);
    }
    // Mermaid diagram has special rendering (not config-driven)
    if (toolName === 'generateMermaidDiagramTool' || toolName === 'generate-mermaid-diagram' || toolName === 'generateMermaidDiagram') {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(GenerateMermaidDiagramToolCall, {
                output: isGenerateMermaidDiagramOutput(part.output) ? part.output : undefined,
                isRunning: isRunning
            })
        }, `${messageId}-tool-${index}`);
    }
    // Corporate Family Tree has specialized UI (mermaid diagram, subsidiaries, confidence)
    if (toolName === 'corporate-family-tree' || toolName === 'corporateFamilyTreeTool') {
        const output = part.output;
        const isValidOutput = isCorporateFamilyTreeOutput(output) || isCorporateFamilyTreeErrorOutput(output);
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(CorporateFamilyTreeToolCall, {
                output: isValidOutput ? output : undefined,
                isRunning: isRunning
            })
        }, `${messageId}-tool-${index}`);
    }
    // Saved history from before creates were proposals: those tool results
    // carry the document id they minted. New creates render above, as proposals.
    const officeOut = part.output;
    if (toolName === 'create-office-document' || isOfficeMintTool(toolName) && officeOut?.documentId) {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(CreateOfficeDocumentCard, {
                output: officeOut ?? {}
            })
        }, `${messageId}-tool-${index}`);
    }
    // Try config-driven rendering
    const config = getToolConfig(toolName);
    if (config) {
        const isSpecra = variant === 'specra';
        const card = /*#__PURE__*/ _jsx(ToolCard, {
            config: config,
            output: part.output,
            input: part.input,
            isRunning: isRunning,
            duration: part.duration,
            rawOutput: part.output,
            embedded: isSpecra
        });
        if (isSpecra) {
            // Same error determination the card uses (schema-invalid counts as an
            // error, not just an explicit isErrorResult) so the disclosure's tone and
            // open state match what the card renders inside it. Embedded hides the
            // card's own header, so there's no running UI to reveal — the run stays
            // collapsed while streaming (SpecraToolRunPart opens only on error).
            const isError = classifyToolResult(config, part.output, isRunning) === 'error';
            return /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.assistant.messageTool,
                children: /*#__PURE__*/ _jsx(SpecraToolRunPart, {
                    displayName: config.displayName ?? humanizeToolId(config.id),
                    isRunning: isRunning,
                    isError: isError,
                    duration: part.duration,
                    children: card
                })
            }, `${messageId}-tool-${index}`);
        }
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.assistant.messageTool,
            children: card
        }, `${messageId}-tool-${index}`);
    }
    // Default tool rendering (generic fallback)
    const outputText = part.output ? typeof part.output === 'string' ? part.output : JSON.stringify(part.output, null, 2) : '';
    const outputBlock = part.state === 'output-available' && outputText ? /*#__PURE__*/ _jsx("pre", {
        className: "text-xs overflow-auto max-h-40",
        children: outputText
    }) : null;
    if (variant === 'specra') {
        // A generic (config-less) run has no schema, so error detection is
        // best-effort: an object payload with a truthy `.error`. It has nothing to
        // show until it finishes, so it stays collapsed while running (the summary
        // reads "Running…") and only pops open on error — never an empty open box.
        const isError = Boolean(part.output?.error);
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.assistant.messageTool,
            children: /*#__PURE__*/ _jsx(SpecraToolRunPart, {
                displayName: toolDisplayName(part),
                isRunning: isRunning,
                isError: isError,
                duration: part.duration,
                children: outputBlock
            })
        }, `${messageId}-tool-${index}`);
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "border rounded-md p-3 bg-muted/50",
        "data-testid": Testids.assistant.messageTool,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "text-sm font-medium mb-1",
                children: toolDisplayName(part)
            }),
            outputBlock
        ]
    }, `${messageId}-tool-${index}`);
}
